// DivisionPage "Table" view (owner 2026-10-09): every game in a shadcn table
// with inline-editable Home/Away score cells and ONE "Save changes (N)" button
// that saves all edited rows at once — faster bulk score entry than the grid.
// Scores only are inline-editable; the pencil opens the existing Edit Game
// modal for full edits. Reuses the gameStore `updateGame` save path (which the
// API uses to advance playoff brackets server-side) + `refreshGames`.
import React, { useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table';
import { Pencil, Loader2, Table2 } from 'lucide-react';
import { buildScoreTableRows, type ScoreTableRow } from './scoreTableRows';

type Side = 'home' | 'away';
type ScoreEdit = { home: string; away: string };

interface GamesScoreTableProps {
  divisionGames: any[];
  divisionTeams: any[];
  updateGame: (game: any, cb?: (id?: string) => void) => Promise<unknown> | void;
  refreshGames?: () => Promise<unknown> | void;
  onEditGame?: (game: any) => void;
}

const norm = (v: unknown): string => (v === '' || v == null ? '' : String(v));

export default function GamesScoreTable({
  divisionGames,
  divisionTeams,
  updateGame,
  refreshGames,
  onEditGame,
}: GamesScoreTableProps) {
  const rows = useMemo(() => buildScoreTableRows(divisionGames, divisionTeams), [divisionGames, divisionTeams]);
  // Only touched rows live here, keyed by game id: { home, away } as strings.
  const [edits, setEdits] = useState<Record<string, ScoreEdit>>({});
  const [saving, setSaving] = useState(false);

  const shown = useCallback(
    (row: ScoreTableRow, side: Side): string => {
      const e = edits[row.id];
      if (e) return e[side];
      const score = side === 'home' ? row.homeScore : row.awayScore;
      return score == null ? '' : String(score);
    },
    [edits],
  );

  const setVal = useCallback(
    (row: ScoreTableRow, side: Side, raw: string) => {
      const v = raw.replace(/[^0-9]/g, '');
      setEdits((prev) => {
        const cur: ScoreEdit = prev[row.id] || {
          home: row.homeScore == null ? '' : String(row.homeScore),
          away: row.awayScore == null ? '' : String(row.awayScore),
        };
        return { ...prev, [row.id]: { ...cur, [side]: v } };
      });
    },
    [],
  );

  const dirtyRows = useMemo(
    () => rows.filter((r) => {
      const e = edits[r.id];
      if (!e) return false;
      return norm(e.home) !== norm(r.homeScore) || norm(e.away) !== norm(r.awayScore);
    }),
    [rows, edits],
  );

  const handleSave = useCallback(async () => {
    if (saving || dirtyRows.length === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        dirtyRows.map((r) => {
          const e = edits[r.id];
          return updateGame({
            ...r.game,
            homeScore: e.home === '' ? null : Number(e.home),
            awayScore: e.away === '' ? null : Number(e.away),
          });
        }),
      );
      setEdits({});
      // Refetch once so placeholders filled by server-side bracket advancement
      // appear, and the shared `games` slice (and the grid view) stay in sync.
      if (refreshGames) await refreshGames();
      toast.success(`Saved ${dirtyRows.length} game${dirtyRows.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error('Failed to save some scores');
    } finally {
      setSaving(false);
    }
  }, [saving, dirtyRows, edits, updateGame, refreshGames]);

  if (!rows.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Table2 className="mx-auto h-7 w-7 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-medium text-foreground">No games yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create or auto-populate games from the Schedule view, then enter scores here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Scores</CardTitle>
          <CardDescription>
            Type scores directly, then save all at once. Use the pencil to edit teams, date, time, or location.
          </CardDescription>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving || dirtyRows.length === 0}>
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
          {dirtyRows.length > 0 ? `Save changes (${dirtyRows.length})` : 'Save changes'}
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Round</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead className="text-right">Home</TableHead>
              <TableHead className="text-center">Score</TableHead>
              <TableHead>Away</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} className={row.isCancelled ? 'opacity-50' : undefined}>
                <TableCell className="whitespace-nowrap text-xs font-medium text-muted-foreground">
                  {row.label}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm">{row.date}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{row.time}</TableCell>
                <TableCell className="max-w-[180px] truncate text-right text-sm font-medium">
                  {row.homeName}
                </TableCell>
                <TableCell>
                  {row.isCancelled ? (
                    <div className="text-center text-xs uppercase text-muted-foreground">Cancelled</div>
                  ) : (
                    <div className="flex items-center justify-center gap-1">
                      <Input
                        type="text"
                        inputMode="numeric"
                        aria-label={`${row.homeName} score`}
                        className="h-8 w-12 text-center"
                        value={shown(row, 'home')}
                        onChange={(e) => setVal(row, 'home', e.target.value)}
                      />
                      <span className="text-muted-foreground">–</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        aria-label={`${row.awayName} score`}
                        className="h-8 w-12 text-center"
                        value={shown(row, 'away')}
                        onChange={(e) => setVal(row, 'away', e.target.value)}
                      />
                    </div>
                  )}
                </TableCell>
                <TableCell className="max-w-[180px] truncate text-sm font-medium">{row.awayName}</TableCell>
                <TableCell className="max-w-[160px] truncate text-sm text-muted-foreground">
                  {row.location}
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Edit game"
                    onClick={() => onEditGame && onEditGame(row.game)}
                  >
                    <Pencil size={14} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
